import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    // You could also log to an analytics service here
    if (window.navigator.onLine) {
      // Only log if online to avoid issues with offline storage
      try {
        // Log to console for now, but you could send to a service
        console.group('React Error Boundary Caught an Error');
        console.error(error);
        console.error(errorInfo.componentStack);
        console.groupEnd();
      } catch (loggingError) {
        console.error('Failed to log error:', loggingError);
      }
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="error-boundary p-4 bg-red-50 dark:bg-red-900/20 rounded-md shadow-md">
          <h1 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Något gick fel</h1>
          <p className="text-red-700 dark:text-red-400 mb-4">
            Vi ber om ursäkt för detta problem. Försök ladda om sidan.
          </p>
          <div className="flex gap-2">
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Ladda om
            </button>
            <button 
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-on-surface/60 text-white rounded hover:bg-on-surface/70 transition-colors"
            >
              Gå tillbaka
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 dark:text-red-200 rounded overflow-auto max-h-64 text-xs font-mono">
              <p className="font-bold">{this.state.error?.name}: {this.state.error?.message}</p>
              <p className="mt-2">{this.state.error?.stack}</p>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 